import { useState } from 'react'

import CircleIcon from '@mui/icons-material/Circle'
import {
  Button,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@mui/material'

export type AgentStatus = 'online' | 'away' | 'offline'

interface AgentStatusToggleProps {
  status: AgentStatus
  onStatusChange: (status: AgentStatus) => void
}

const statusConfig = {
  online: {
    label: 'Online',
    color: 'success.main',
    description: 'Available for new conversations',
  },
  away: {
    label: 'Away',
    color: 'warning.main',
    description: 'Not accepting new conversations',
  },
  offline: {
    label: 'Offline',
    color: 'error.main',
    description: 'Not available',
  },
}

export const AgentStatusToggle = ({ status, onStatusChange }: AgentStatusToggleProps) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const open = Boolean(anchorEl)

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const handleStatusSelect = (newStatus: AgentStatus) => {
    onStatusChange(newStatus)
    handleClose()
  }

  const currentConfig = statusConfig[status]

  return (
    <>
      <Button
        variant="outlined"
        size="small"
        onClick={handleClick}
        startIcon={
          <CircleIcon
            sx={{
              fontSize: 12,
              color: currentConfig.color,
            }}
          />
        }
        sx={{
          textTransform: 'none',
          borderColor: 'divider',
        }}
      >
        {currentConfig.label}
      </Button>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        {(Object.keys(statusConfig) as AgentStatus[]).map((statusKey) => {
          const config = statusConfig[statusKey]
          return (
            <MenuItem
              key={statusKey}
              onClick={() => handleStatusSelect(statusKey)}
              selected={status === statusKey}
            >
              <ListItemIcon>
                <CircleIcon
                  sx={{
                    fontSize: 12,
                    color: config.color,
                  }}
                />
              </ListItemIcon>
              <ListItemText>
                <Typography variant="body2">{config.label}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {config.description}
                </Typography>
              </ListItemText>
            </MenuItem>
          )
        })}
      </Menu>
    </>
  )
}
